import { IconClock } from '@/assets/icons';
import { Images, ImagesMobile } from '@/assets/images';
import { Select } from '@/components';
import Button from '@/components/common/button';
import { MasterInfoEnum, PROGRAM_OPTION_SELECT_ALL, ROUTERS } from '@/constant';
import { useCommonList } from '@/hooks';
import { MenuContext, useSessionTimerContext } from '@/routers/main-layout';
import { AuthActions, CommonActions, CommonSelectors } from '@/stores';
import { MyPageSelectors } from '@/stores/my-page';
import { Colors } from '@/themes';
import { cls, NOOP, ONE_MONTH_IN_MS, showAlert } from '@/utils';
import { isMobile } from '@/utils/mobile.util';
import { memo, ReactNode, useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { ConfigProvider, Segmented } from 'antd';
import { DefaultOptionType } from 'antd/es/select';

import './style.scss';
import { includes } from 'ramda';
import { isNotNilOrEmpty } from 'ramda-adjunct';

import { getHeaderOptions } from './config';

const ItemRender = ({
	text,
	onClick,
	hidden,
	hiddenVerticalLine,
	customClassName,
}: {
	text: ReactNode;
	onClick?: () => void;
	hidden?: boolean;
	hiddenVerticalLine?: boolean;
	customClassName?: string | boolean;
}) => {
	if (hidden) {
		return null;
	}
	return (
		<>
			{text ? (
				typeof text === 'string' || typeof text === 'number' ? (
					<div
						className={cls('cursor-pointer', customClassName)}
						onClick={onClick}
					>
						{text || '-'}
					</div>
				) : (
					text
				)
			) : (
				'-'
			)}
			{!hiddenVerticalLine && <div className="w-px h-4 bg-gray1"></div>}
		</>
	);
};

const Header = () => {
	const menuContext = useContext(MenuContext);

	const { t } = useTranslation(['common', 'validation']);

	const {
		isSystemRole,
		isWorkerRole,
		isWorkerMasterRole,
		isWorkerAdminOrPublicAdminRole,
		isForeignRole,
		isForeignMasterRole,
		name,
		departmentName,
		unitName,
		unitManagerName,
	} = useSelector(MyPageSelectors.selectDataMyPage);

	const hasFormChanged = useSelector(CommonSelectors.selectFormHasChanged);

	const headerProgram = useSelector(CommonSelectors.selectHeaderProgram);

	const { pathname } = useLocation();

	const dispatch = useDispatch();
	const navigate = useNavigate();

	const { selectMasterInfo: workerProgramList } = useCommonList(
		MasterInfoEnum.PROGRAM,
		isWorkerRole || false
	);

	const { selectMasterInfo: foreignProgramList } = useCommonList(
		MasterInfoEnum.PROGRAM_OF_FOREIGN,
		isForeignRole || false
	);

	const programListOptions = useMemo(() => {
		if (isWorkerRole) {
			return [PROGRAM_OPTION_SELECT_ALL, ...(workerProgramList.data || [])];
		}
		if (isForeignRole) {
			return [PROGRAM_OPTION_SELECT_ALL, ...(foreignProgramList.data || [])];
		}

		return [PROGRAM_OPTION_SELECT_ALL];
	}, [
		workerProgramList.data,
		foreignProgramList.data,
		isWorkerRole,
		isForeignRole,
	]);

	const programValue = useMemo(
		() =>
			programListOptions?.find((p) => p?.value === headerProgram?.value) || '',
		[programListOptions, headerProgram?.value]
	);

	const options = useMemo(
		() => getHeaderOptions(isSystemRole, isWorkerRole),
		[isSystemRole, isWorkerRole]
	);

	const onTabItemChange = useCallback((value: string) => {
		navigate(value);
	}, []);

	const defaultValue = useMemo(
		() =>
			options?.find((op) => includes(op.value.split('/')[1], pathname))?.value,
		[options, pathname]
	);

	const profileName = useMemo(() => {
		if (isWorkerMasterRole || isForeignMasterRole) {
			return `${unitManagerName} (${unitName})`;
		}

		return `${name} ${
			isNotNilOrEmpty(departmentName) ? `(${departmentName})` : ''
		}`;
	}, [
		isWorkerMasterRole,
		isForeignMasterRole,
		unitManagerName,
		unitName,
		name,
		departmentName,
	]);

	const isHideHeader = false;

	// const isHideHeader = useMemo(
	// 	() => hideTabHeaderList?.every((_) => !includes(_, pathname)),
	// 	[pathname]
	// );

	// const isHideAllProgram = useMemo(
	// 	() => !includes(pathname, screenHasAllProgramList),
	// 	[pathname]
	// );

	const callHasChanged = useCallback(
		(path: string) => {
			if (hasFormChanged) {
				showAlert(
					t('validation:CONFIRM_21'),
					() => {
						navigate(path);
						dispatch(CommonActions.setHasFormChange(false));
					},
					NOOP,
					{
						cancelText: 'cancel',
						confirmText: 'confirm-2',
					}
				);
			} else {
				navigate(path);
			}
		},
		[hasFormChanged, t]
	);

	const onProfileClick = useCallback(() => {
		if (includes(ROUTERS.MP1_01, pathname)) return;
		callHasChanged(ROUTERS.MP1_01);
	}, [pathname, callHasChanged]);

	const onUnitNameClick = useCallback(() => {
		if (includes(ROUTERS.FM1_01, pathname)) return;
		navigate(ROUTERS.FM1_01);
	}, [pathname]);

	const setHeaderProgram = useCallback(
		(value: string, option: DefaultOptionType) => {
			const now = Date.now();
			const expiry = now + ONE_MONTH_IN_MS;
			dispatch(CommonActions.setHeaderProgram({ option, expiry }));
		},
		[]
	);

	const onLogoClick = useCallback(() => {
		callHasChanged(ROUTERS.MAIN);
	}, [callHasChanged]);

	const onLogoutClick = useCallback(() => {
		// TODO: Alert
		dispatch(AuthActions.logoutRequest({ isCallApi: true }));
	}, []);

	const { timer, onExtend, extendLoading } = useSessionTimerContext();

	return (
		<div className="h-[54px] md:h-[62px] xl:h-[70px] px-[15px] md:px-[52px] xl:px-[30px] flex items-center justify-between relative bg-blue2">
			<div className="flex items-center gap-x-[30px]">
				<Button
					className="hidden lg:block min-w-max"
					type="text"
					icon={<img src={Images.iconMenuHamburger} alt="menu-hamburger" />}
					onClick={menuContext.onToggle}
				/>

				{!isSystemRole && !isMobile && (
					<div className="flex items-center gap-x-2">
						<Select
							className="custom-select"
							popupClassName="!w-[300px]"
							dropdownAlign={{ offset: [0, 20] }}
							options={programListOptions}
							value={programValue}
							// disabled={isHideAllProgram}
							onChange={setHeaderProgram}
							translationLimit={1}
						/>
					</div>
				)}
				{isHideHeader && (
					<ConfigProvider
						theme={{
							token: {
								/* here is your global tokens */
								controlHeight: 40,
								fontSize: 15,
							},
							components: {
								Segmented: {
									/* here is your component tokens */
									itemColor: Colors.palette.white,
									itemSelectedBg: Colors.palette.white,
									itemHoverBg: Colors.palette.white,
									itemSelectedColor: Colors.palette.rhino,
									trackBg: Colors.palette.secondary,
									trackPadding: 0,
								},
							},
						}}
					>
						<Segmented
							options={options}
							defaultValue={defaultValue}
							value={defaultValue}
							onChange={onTabItemChange}
							disabled
							block
						/>
					</ConfigProvider>
				)}
			</div>
			<img
				className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 cursor-pointer w-[238px] h-[22px] md:w-[270px] md:h-[21px] xl:w-[363px] xl:h-[32px]"
				src={
					isForeignRole
						? ImagesMobile.iconLogoBuyeoMiniForeign
						: ImagesMobile.iconLogoBuyeoMini
				}
				alt="logo"
				onClick={onLogoClick}
			/>

			<div className="hidden lg:flex items-center gap-x-5 text-base text-white">
				<ItemRender
					text={profileName}
					onClick={onProfileClick}
					customClassName="max-w-[300px] truncate"
				/>

				<ItemRender
					hidden={isMobile}
					text={
						<div className="flex items-center gap-1.5 rounded-lg">
							{/* <img
								src={Images.iconClock}
								alt="session-timer"
								className="h-6 w-6 shrink-0"
							/> */}
							<IconClock className="h-6 w-6 shrink-0" />
							<span className="min-w-[48px] text-base font-semibold text-[#FFE168]">
								{timer}
							</span>
							<Button
								type="default"
								disabled={extendLoading}
								className="!h-7 !rounded-[4px] !bg-white px-[14px] !text-base !font-semibold !text-blue2 !border-none ml-1"
								onClick={onExtend}
							>
								{t('extend')}
							</Button>
						</div>
					}
				/>

				<ItemRender
					text={unitName}
					onClick={onUnitNameClick}
					hidden={true}
					customClassName={
						isWorkerAdminOrPublicAdminRole && 'pointer-events-none'
					}
				/>

				<ItemRender
					text={t('logout')}
					onClick={onLogoutClick}
					hiddenVerticalLine
				/>
			</div>
			<div className="block lg:hidden" onClick={menuContext.onToggle}>
				<img
					src={ImagesMobile.iconHamburger}
					alt="menu-hamburger"
					className="w-[24px] h-[24px]"
				/>
			</div>
		</div>
	);
};

export default memo(Header);
